import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import DataGrid, { Row } from "react-data-grid";
import "react-data-grid/lib/styles.css";
import "../GridCore.css";
import HeavyCellWrapper from "../cells/HeavyCellWrapper";
import { useGridContainer } from "./useGridContainer";
import { useGridEditing } from "../hooks/useGridEditing";
import { GridTooltipProvider, useGridTooltip } from "../hooks/useGridTooltip";
import { GRID_SELECTION_CONTROLLER } from "../selection/useGridSelection";
import { GridStateProvider, useGridStateStore } from "../state/GridStateContext";
import { joinClassNames } from "../utils/classNames";

const DEFAULT_ROW_HEIGHT = 36;
const DEFAULT_HEADER_ROW_HEIGHT = 34;
const DEFAULT_ROW_GAP_HEIGHT = 8;
const DEFAULT_ROW_GAP_COLOR = "#f1f5f9";
const DATA_ROW_SELECTOR = "[data-rdg-row-idx]";
const DATA_CELL_SELECTOR = '[role="gridcell"][aria-colindex]';
const SORT_INDICATORS = Object.freeze({ asc: "▲", desc: "▼" });

function isColumnGroup(column) {
    return Array.isArray(column?.children);
}

function flattenLeafColumns(columns) {
    const leafColumns = [];
    columns.forEach((column) => {
        if (isColumnGroup(column)) {
            leafColumns.push(...flattenLeafColumns(column.children));
        } else {
            leafColumns.push(column);
        }
    });
    return leafColumns;
}

function areArraysShallowEqual(left = [], right = []) {
    return left.length === right.length && left.every((item, index) => item === right[index]);
}

function resolveColumnEditable(column, row) {
    if (!column) return false;
    if (typeof column.editable === "function") return column.editable(row);
    return column.editable === true;
}

function getRowIndex(rows, row, rowKeyGetter) {
    const fallbackIndex = rows.indexOf(row);
    if (!rowKeyGetter) return fallbackIndex;

    const rowKey = rowKeyGetter(row);
    const keyedIndex = rows.findIndex((item) => rowKeyGetter(item) === rowKey);
    return keyedIndex >= 0 ? keyedIndex : fallbackIndex;
}

function isSameCell(cell, rowIdx, colIdx) {
    return cell?.rowIdx === rowIdx && cell?.colIdx === colIdx;
}

function resolveCellClass(cellClass, row) {
    return typeof cellClass === "function" ? cellClass(row) : cellClass;
}

function canSelectColumnHeader(column) {
    return column?.frozen !== true && column?.isRowHeader !== true && column?.excludeFromSelection !== true;
}

function canSelectDataCell(column) {
    return column?.isRowHeader !== true && column?.excludeFromSelection !== true;
}

function normalizeColumnWidthConstraints(constraints) {
    if (!constraints) return null;
    const minWidth = Number.isFinite(Number(constraints.minWidth)) ? Number(constraints.minWidth) : undefined;
    const maxWidth = Number.isFinite(Number(constraints.maxWidth)) ? Number(constraints.maxWidth) : undefined;
    if (minWidth === undefined && maxWidth === undefined) return null;
    return {
        minWidth,
        maxWidth: maxWidth === undefined || minWidth === undefined ? maxWidth : Math.max(minWidth, maxWidth),
    };
}

function clampColumnWidth(width, constraints) {
    const numericWidth = Number(width);
    if (!Number.isFinite(numericWidth)) return null;
    if (!constraints) return Math.round(numericWidth);
    const minWidth = constraints.minWidth ?? Number.NEGATIVE_INFINITY;
    const maxWidth = constraints.maxWidth ?? Number.POSITIVE_INFINITY;
    return Math.min(Math.max(Math.round(numericWidth), minWidth), maxWidth);
}

function applyColumnRuntimeState(column, columnState, columnWidthConstraints) {
    const width = clampColumnWidth(columnState?.width?.[column.key], columnWidthConstraints);
    const nextColumn = width === null ? { ...column } : { ...column, width };
    if (column.resizable === false || !columnWidthConstraints) return nextColumn;
    if (columnWidthConstraints.minWidth !== undefined) nextColumn.minWidth = columnWidthConstraints.minWidth;
    if (columnWidthConstraints.maxWidth !== undefined) nextColumn.maxWidth = columnWidthConstraints.maxWidth;
    return nextColumn;
}

function shouldIgnoreSortableHeaderClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target === event.currentTarget) return false;
    const interactiveElement = target.closest("button, a, input, select, textarea, [role='button'], [data-grid-core-ignore-sort]");
    return Boolean(interactiveElement && interactiveElement !== event.currentTarget);
}

function isPrintableEditKey(event) {
    return event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
}

function isKeyboardEditTrigger(event) {
    if (event.key === "Backspace") return !event.ctrlKey && !event.metaKey && !event.altKey;
    return isPrintableEditKey(event);
}

function getKeyboardInitialValue(event) {
    if (event.key === "Backspace") return "";
    if (isPrintableEditKey(event)) return event.key;
    return undefined;
}

function getColumnEditorType(column) {
    return column.editorType ?? column.editorProps?.editorType ?? column.editorOptions?.editorType ?? "text";
}

function getColumnEditorProps(column) {
    return column.editorProps ?? column.editorOptions;
}

function normalizeEditorValue(column, value, row) {
    if (column.normalize) return column.normalize(value, row);
    return value;
}

function mergeEditorColumn(column, editorProps) {
    if (!editorProps) return column;

    const { editorOptions, ...columnProps } = editorProps;
    return {
        ...column,
        ...columnProps,
        editorOptions: {
            ...column.editorOptions,
            ...columnProps,
            ...editorOptions,
        },
    };
}

function buildEditorColumn(column, editorType, editorProps, hasInitialValue) {
    const mergedColumn = mergeEditorColumn(column, editorProps);
    if (editorType !== "textarea" && !hasInitialValue) return mergedColumn;

    return {
        ...mergedColumn,
        editorOptions: {
            ...mergedColumn.editorOptions,
            ...(editorType === "textarea" ? { commitOnEnter: true } : null),
            ...(hasInitialValue ? { hasInitialValue: true, selectOnFocus: false } : null),
        },
    };
}

function getEditorDomValue(event) {
    const target = event.target instanceof Element ? event.target : null;
    const input = target?.closest("input, textarea");
    return input && "value" in input ? input.value : undefined;
}

function scheduleAfterPaint(callback) {
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(callback);
        return;
    }
    setTimeout(callback, 0);
}

function buildEnhancedColumns({
    columns,
    columnState,
    normalizedColumnWidthConstraints,
    editable,
    onColumnHeaderClick,
    sortController,
    gridStateStore,
    rowsRef,
    rowKeyGetterRef,
    editRuntimeRef,
    previousCache,
}) {
    const leafByKey = new Map();
    const groupsByPath = new Map();
    let leafIndex = 0;

    const enhanceColumn = (column, path) => {
        if (isColumnGroup(column)) {
            const children = column.children.map((child, childIndex) => enhanceColumn(child, `${path}.${childIndex}`));
            const previousGroup = previousCache?.groupsByPath.get(path);
            if (previousGroup?.sourceColumn === column && areArraysShallowEqual(previousGroup.children, children)) {
                groupsByPath.set(path, previousGroup);
                return previousGroup.enhancedColumn;
            }

            const enhancedColumn = { ...column, children };
            groupsByPath.set(path, { sourceColumn: column, children, enhancedColumn });
            return enhancedColumn;
        }

        const colIdx = leafIndex;
        leafIndex += 1;
        const cacheKey = column.key ?? path;
        const columnWithState = applyColumnRuntimeState(column, columnState, normalizedColumnWidthConstraints);
        const previousLeaf = previousCache?.leafByKey.get(cacheKey);
        if (
            previousLeaf?.sourceColumn === column &&
            previousLeaf.colIdx === colIdx &&
            previousLeaf.width === columnWithState.width &&
            previousLeaf.minWidth === columnWithState.minWidth &&
            previousLeaf.maxWidth === columnWithState.maxWidth &&
            previousLeaf.editable === editable &&
            previousLeaf.normalizedColumnWidthConstraints === normalizedColumnWidthConstraints &&
            previousLeaf.onColumnHeaderClick === onColumnHeaderClick &&
            previousLeaf.sortController === sortController &&
            previousLeaf.gridStateStore === gridStateStore
        ) {
            leafByKey.set(cacheKey, previousLeaf);
            return previousLeaf.enhancedColumn;
        }

        const originalCellClass = columnWithState.cellClass;
        const originalRenderHeaderCell = columnWithState.renderHeaderCell;
        const columnHeaderSelectable = Boolean(onColumnHeaderClick && canSelectColumnHeader(columnWithState));
        const columnSortable = Boolean(sortController?.isColumnSortable?.(columnWithState.key));
        const renderHeaderCell = columnSortable
            ? (props) => {
                  const headerColumn = props.column ?? columnWithState;
                  const content = originalRenderHeaderCell ? originalRenderHeaderCell(props) : columnWithState.name;
                  const sortOrder = sortController?.getColumnSortOrder?.(headerColumn.key);
                  const columnLabel = typeof columnWithState.name === "string" ? columnWithState.name : columnWithState.key;
                  return (
                      <div
                          className="grid-core-sort-header-button"
                          role="button"
                          tabIndex={0}
                          title={`排序 ${columnLabel}`}
                          aria-label={`排序 ${columnLabel}`}
                          aria-pressed={Boolean(sortOrder)}
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                              if (shouldIgnoreSortableHeaderClick(event)) {
                                  event.stopPropagation();
                                  return;
                              }
                              event.preventDefault();
                              event.stopPropagation();
                              sortController?.toggleColumnSort?.(headerColumn.key);
                          }}
                          onKeyDown={(event) => {
                              if (event.key !== "Enter" && event.key !== " ") return;
                              event.preventDefault();
                              event.stopPropagation();
                              sortController?.toggleColumnSort?.(headerColumn.key);
                          }}
                      >
                          <span className="grid-core-sort-header-label">{content}</span>
                          {sortOrder && <span className="grid-core-sort-indicator" aria-hidden="true">{SORT_INDICATORS[sortOrder]}</span>}
                      </div>
                  );
              }
            : columnHeaderSelectable
            ? (props) => {
                  const content = originalRenderHeaderCell ? originalRenderHeaderCell(props) : columnWithState.name;
                  return (
                      <button
                          type="button"
                          className="grid-core-header-select-button"
                          title={`选择 ${typeof columnWithState.name === "string" ? columnWithState.name : columnWithState.key} 列`}
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              onColumnHeaderClick(props.column ?? columnWithState, event);
                          }}
                      >
                          {content}
                      </button>
                  );
              }
            : originalRenderHeaderCell;
        const cellClass = (row) => {
            const rowIdx = getRowIndex(rowsRef.current, row, rowKeyGetterRef.current);
            return joinClassNames(
                resolveCellClass(originalCellClass, row),
                gridStateStore.getCellClassName(rowIdx, colIdx),
            );
        };

        const enhancedColumn = !editable || !columnWithState.editable
            ? {
                  ...columnWithState,
                  cellClass,
                  renderHeaderCell,
                  editable: false,
                  renderEditCell: undefined,
              }
            : {
                  ...columnWithState,
                  cellClass,
                  renderHeaderCell,
                  renderEditCell: (props) => {
                      const editColIdx = props.column.idx;
                      const editRuntime = editRuntimeRef.current;
                      const cellIsEditing = isSameCell(editRuntime?.editingCell, props.rowIdx, editColIdx);
                      const editorType = getColumnEditorType(columnWithState);
                      const hasEditorInitialValue = Boolean(cellIsEditing && editRuntime?.hasInitialValue);
                      const editorColumn = buildEditorColumn(
                          columnWithState,
                          editorType,
                          getColumnEditorProps(columnWithState),
                          hasEditorInitialValue,
                      );
                      const editorRow = hasEditorInitialValue
                          ? { ...props.row, [props.column.key]: editRuntime?.initialValue }
                          : props.row;
                      return (
                          <HeavyCellWrapper
                              {...props}
                              row={editorRow}
                              column={editorColumn}
                              editorType={editorType}
                          />
                      );
                  },
              };

        leafByKey.set(cacheKey, {
            sourceColumn: column,
            colIdx,
            width: columnWithState.width,
            minWidth: columnWithState.minWidth,
            maxWidth: columnWithState.maxWidth,
            editable,
            normalizedColumnWidthConstraints,
            onColumnHeaderClick,
            sortController,
            gridStateStore,
            enhancedColumn,
        });
        return enhancedColumn;
    };

    const nextColumns = columns.map((column, index) => enhanceColumn(column, String(index)));
    const enhancedColumns = previousCache && areArraysShallowEqual(previousCache.columns, nextColumns)
        ? previousCache.columns
        : nextColumns;

    return {
        columns: enhancedColumns,
        leafByKey,
        groupsByPath,
    };
}

function resolveActiveFocusCell(controlledFocusCell, selection, internalFocusCell) {
    if (controlledFocusCell !== undefined) return controlledFocusCell;
    if (selection !== undefined) return selection.focus ?? null;
    return internalFocusCell;
}

function getSelectionController(selection) {
    return selection?.[GRID_SELECTION_CONTROLLER] ?? null;
}

const GridShell = forwardRef(function GridShell(
    {
        rows = [],
        columns = [],
        rowKeyGetter = (row) => row.id,
        rowHeight = DEFAULT_ROW_HEIGHT,
        headerRowHeight = DEFAULT_HEADER_ROW_HEIGHT,
        rowClass,
        rowGapHeight = DEFAULT_ROW_GAP_HEIGHT,
        rowGapColor = DEFAULT_ROW_GAP_COLOR,
        fixedAreaRef,
        minHeight,
        bottomOffset = 0,
        className,
        gridClassName,
        renderers,
        noRowsFallback,
        style,
        onCellClick,
        onCellDoubleClick,
        onCellKeyDown,
        onSelectedCellChange,
        onColumnHeaderClick,
        onColumnResize,
        onRowsChange,
        onCommit,
        editable = false,
        focusCell: controlledFocusCell,
        selection,
        onFocusCellChange,
        columnState,
        columnWidthConstraints,
        sortController,
        ...dataGridProps
    },
    ref,
) {
    const dataGridRef = useRef(null);
    const rowsRef = useRef(rows);
    const rowKeyGetterRef = useRef(rowKeyGetter);
    const leafColumnsRef = useRef([]);
    const focusCellRef = useRef(null);
    const editRuntimeRef = useRef(null);
    const enhancedColumnsCacheRef = useRef(null);
    const dragSelectionRef = useRef(null);
    const mouseDownCellRef = useRef(null);
    const pendingNavigationRef = useRef(null);
    const silentSelectedCellRef = useRef(null);
    const [internalFocusCell, setInternalFocusCell] = useState(null);
    const [scrollLocked, setScrollLocked] = useState(false);
    const { containerRef, height, recalculateHeight } = useGridContainer({ fixedAreaRef, minHeight, bottomOffset });
    const { showTooltip, hideTooltip, tooltip } = useGridTooltip();
    const normalizedRowGapHeight = Number(rowGapHeight) > 0 ? Number(rowGapHeight) : 0;
    const activeFocusCell = resolveActiveFocusCell(controlledFocusCell, selection, internalFocusCell);
    const selectionController = getSelectionController(selection);
    const normalizedColumnWidthConstraints = useMemo(
        () => normalizeColumnWidthConstraints(columnWidthConstraints),
        [columnWidthConstraints],
    );

    rowsRef.current = rows;
    rowKeyGetterRef.current = rowKeyGetter;
    focusCellRef.current = activeFocusCell;

    const lockScroll = useCallback(() => setScrollLocked(true), []);
    const unlockScroll = useCallback(() => setScrollLocked(false), []);

    const syncFocusCell = useCallback(
        (rowIdx, colIdx, row, column) => {
            const nextFocusCell = {
                rowIdx,
                colIdx,
                row,
                column,
                columnKey: column?.key,
            };
            focusCellRef.current = nextFocusCell;
            setInternalFocusCell(nextFocusCell);
            onFocusCellChange?.(nextFocusCell);
        },
        [onFocusCellChange],
    );

    const isGridCellEditable = useCallback(
        (rowIdx, colIdx) => {
            if (!editable) return false;
            return resolveColumnEditable(leafColumnsRef.current[colIdx], rowsRef.current[rowIdx]);
        },
        [editable],
    );

    const { editingCell, enterEdit, exitEdit, isEditing, hasInitialValue, initialValue } = useGridEditing({
        focusCell: activeFocusCell,
        isCellEditable: isGridCellEditable,
    });
    const gridStateSnapshot = useMemo(
        () => ({ selection, focus: activeFocusCell, editingCell }),
        [activeFocusCell, editingCell, selection],
    );
    const gridStateStore = useGridStateStore(gridStateSnapshot);

    const getCellPositionFromMouseEvent = useCallback(
        (event) => {
            const target = event.target instanceof Element ? event.target : null;
            const containerElement = containerRef.current;
            if (!target || !containerElement) return null;

            const cellElement = target.closest(DATA_CELL_SELECTOR);
            if (!cellElement || !containerElement.contains(cellElement)) return null;

            const rowElement = cellElement.closest(DATA_ROW_SELECTOR);
            if (!rowElement || !containerElement.contains(rowElement)) return null;

            const rowIdx = Number(rowElement.dataset.rdgRowIdx);
            const colIdx = Number(cellElement.getAttribute("aria-colindex")) - 1;
            const row = rowsRef.current[rowIdx];
            const column = leafColumnsRef.current[colIdx];
            if (row === undefined || !column || !Number.isInteger(rowIdx) || !Number.isInteger(colIdx)) return null;

            return { rowIdx, colIdx, row, column };
        },
        [containerRef],
    );

    const clearSilentSelectedCell = useCallback((target) => {
        scheduleAfterPaint(() => {
            if (isSameCell(silentSelectedCellRef.current, target.rowIdx, target.colIdx)) {
                silentSelectedCellRef.current = null;
            }
        });
    }, []);

    const selectFocusCell = useCallback(
        (rowIdx, colIdx, options = {}) => {
            const row = rowsRef.current[rowIdx];
            const column = leafColumnsRef.current[colIdx];
            if (row === undefined || !column) return;

            if (options.suppressSelectedCellChange) {
                silentSelectedCellRef.current = { rowIdx, colIdx };
                clearSilentSelectedCell(silentSelectedCellRef.current);
            }
            dataGridRef.current?.selectCell?.({ rowIdx, idx: colIdx });
            syncFocusCell(rowIdx, colIdx, row, column);
        },
        [clearSilentSelectedCell, syncFocusCell],
    );

    const finishDragSelection = useCallback(() => {
        dragSelectionRef.current = null;
    }, []);

    useEffect(() => {
        window.addEventListener("mouseup", finishDragSelection);
        return () => {
            window.removeEventListener("mouseup", finishDragSelection);
        };
    }, [finishDragSelection]);

    const getNavigationTarget = useCallback(
        (direction, rowIdx, colIdx) => {
            if (direction === "enter") {
                const nextRowIdx = rowIdx + 1;
                if (nextRowIdx >= rowsRef.current.length) return null;
                return { rowIdx: nextRowIdx, colIdx };
            }

            const step = direction === "shiftTab" ? -1 : 1;
            for (let nextColIdx = colIdx + step; nextColIdx >= 0 && nextColIdx < leafColumnsRef.current.length; nextColIdx += step) {
                if (isGridCellEditable(rowIdx, nextColIdx)) {
                    return { rowIdx, colIdx: nextColIdx };
                }
            }
            return null;
        },
        [isGridCellEditable],
    );

    const runPendingNavigation = useCallback(() => {
        const pendingNavigation = pendingNavigationRef.current;
        if (!pendingNavigation) return;

        pendingNavigationRef.current = null;
        selectFocusCell(pendingNavigation.rowIdx, pendingNavigation.colIdx);
    }, [selectFocusCell]);

    const queueNavigationAfterNativeEdit = useCallback(
        (direction, rowIdx, colIdx) => {
            const target = getNavigationTarget(direction, rowIdx, colIdx);
            if (!target) return;

            pendingNavigationRef.current = target;
            scheduleAfterPaint(runPendingNavigation);
        },
        [getNavigationTarget, runPendingNavigation],
    );

    editRuntimeRef.current = {
        editingCell,
        exitEdit,
        hasInitialValue,
        initialValue,
    };

    useEffect(() => {
        if (!editingCell) return undefined;

        function handleDocumentMouseDown(event) {
            const target = event.target instanceof Element ? event.target : null;
            if (target?.closest(".rdg-editor-container")) return;
            scheduleAfterPaint(() => editRuntimeRef.current?.exitEdit?.());
        }

        window.addEventListener("mousedown", handleDocumentMouseDown, true);
        return () => {
            window.removeEventListener("mousedown", handleDocumentMouseDown, true);
        };
    }, [editingCell]);

    const enhancedColumns = useMemo(() => {
        const nextCache = buildEnhancedColumns({
            columns,
            columnState,
            normalizedColumnWidthConstraints,
            editable,
            onColumnHeaderClick,
            sortController,
            gridStateStore,
            rowsRef,
            rowKeyGetterRef,
            editRuntimeRef,
            previousCache: enhancedColumnsCacheRef.current,
        });
        enhancedColumnsCacheRef.current = nextCache;
        return nextCache.columns;
    }, [
        columnState,
        columns,
        editable,
        gridStateStore,
        normalizedColumnWidthConstraints,
        onColumnHeaderClick,
        sortController,
    ]);

    leafColumnsRef.current = flattenLeafColumns(enhancedColumns);

    useImperativeHandle(
        ref,
        () => ({
            lockScroll,
            unlockScroll,
            scrollToCell(rowIdx, colIdx) {
                dataGridRef.current?.scrollToCell({ rowIdx, idx: colIdx });
            },
            recalculateHeight,
        }),
        [lockScroll, recalculateHeight, unlockScroll],
    );

    const renderRow = useCallback(
        (key, props) => (
            <Row
                key={key}
                {...props}
                data-rdg-row-idx={props.rowIdx}
                data-rdg-row-key={rowKeyGetter?.(props.row)}
            />
        ),
        [rowKeyGetter],
    );

    const getRowHeight = useCallback(
        (row) => {
            const baseRowHeight = typeof rowHeight === "function" ? rowHeight(row) : rowHeight;
            return Number(baseRowHeight || DEFAULT_ROW_HEIGHT) + (row?._rowGapAfter ? normalizedRowGapHeight : 0);
        },
        [normalizedRowGapHeight, rowHeight],
    );

    const getRowClass = useCallback(
        (row) => joinClassNames(typeof rowClass === "function" ? rowClass(row) : rowClass, row?._rowGapAfter && "grid-core-row-gap-after"),
        [rowClass],
    );

    const mergedRenderers = useMemo(
        () => ({
            ...renderers,
            renderRow,
            noRowsFallback: noRowsFallback ?? renderers?.noRowsFallback,
        }),
        [noRowsFallback, renderRow, renderers],
    );

    const handleRowsChange = useCallback(
        (nextRows, data) => {
            onRowsChange?.(nextRows, data);

            const commitColumn = data?.column;
            const commitIndexes = Array.isArray(data?.indexes) ? data.indexes : [];
            if (onCommit && commitColumn && commitIndexes.length > 0) {
                commitIndexes.forEach((rowIdx) => {
                    const nextRow = nextRows[rowIdx];
                    const value = commitColumn.getCommitValue
                        ? commitColumn.getCommitValue(nextRow, rowsRef.current[rowIdx], data)
                        : nextRow?.[commitColumn.key];
                    onCommit(rowIdx, commitColumn.idx, value);
                });
            }

            const editRuntime = editRuntimeRef.current;
            if (commitColumn && commitIndexes.some((rowIdx) => isSameCell(editRuntime?.editingCell, rowIdx, commitColumn.idx))) {
                editRuntime?.exitEdit?.();
                scheduleAfterPaint(runPendingNavigation);
            }
        },
        [onCommit, onRowsChange, runPendingNavigation],
    );

    const commitCurrentEditorDomValue = useCallback(
        (args, event) => {
            const editRuntime = editRuntimeRef.current;
            const colIdx = args.column?.idx;
            if (!isSameCell(editRuntime?.editingCell, args.rowIdx, colIdx)) return;

            const domValue = getEditorDomValue(event);
            const columnKey = args.column?.key;
            const currentRow = rowsRef.current[args.rowIdx];
            if (domValue === undefined || !columnKey || !currentRow) return;

            const editRow = args.row ?? currentRow;
            const normalizedValue = normalizeEditorValue(args.column, domValue, editRow);
            if (Object.is(editRow[columnKey], normalizedValue)) return;

            const nextRows = [...rowsRef.current];
            nextRows[args.rowIdx] = { ...currentRow, [columnKey]: normalizedValue };
            handleRowsChange(nextRows, { indexes: [args.rowIdx], column: args.column });
        },
        [handleRowsChange],
    );

    const handleSelectedCellChange = useCallback(
        (args) => {
            const silentSelectedCell = silentSelectedCellRef.current;
            if (isSameCell(silentSelectedCell, args.rowIdx, args.column.idx)) {
                silentSelectedCellRef.current = null;
                return;
            }

            syncFocusCell(args.rowIdx, args.column.idx, args.row, args.column);
            onSelectedCellChange?.(args);
        },
        [onSelectedCellChange, syncFocusCell],
    );

    const handleColumnResize = useCallback(
        (columnIndexOrColumn, width) => {
            const column = Number.isInteger(columnIndexOrColumn)
                ? leafColumnsRef.current[columnIndexOrColumn]
                : columnIndexOrColumn?.column ?? columnIndexOrColumn;
            const nextWidth = Number(width ?? columnIndexOrColumn?.width);
            if (!column?.key || !Number.isFinite(nextWidth)) return;
            onColumnResize?.(column.key, nextWidth, column);
        },
        [onColumnResize],
    );

    const handleCellClick = useCallback(
        (args, event) => {
            if (mouseDownCellRef.current?.dragged) {
                mouseDownCellRef.current = null;
                event.preventGridDefault();
                return;
            }

            onCellClick?.(args, event);
            if (event.isGridDefaultPrevented()) {
                mouseDownCellRef.current = null;
                return;
            }

            const rowIdx = getRowIndex(rowsRef.current, args.row, rowKeyGetter);
            const colIdx = args.column.idx;
            const mouseDownCell = mouseDownCellRef.current;
            const wasFocusedBeforeMouseDown = isSameCell(mouseDownCell, rowIdx, colIdx)
                ? mouseDownCell.wasFocusedBeforeMouseDown
                : isSameCell(focusCellRef.current, rowIdx, colIdx);
            mouseDownCellRef.current = null;
            if (rowIdx < 0 || !wasFocusedBeforeMouseDown || isEditing(rowIdx, colIdx) || !isGridCellEditable(rowIdx, colIdx)) {
                return;
            }

            if (enterEdit(rowIdx, colIdx, { focusCell: focusCellRef.current })) {
                event.preventGridDefault();
                args.selectCell(true);
            }
        },
        [enterEdit, isEditing, isGridCellEditable, onCellClick, rowKeyGetter],
    );

    const handleGridMouseDownCapture = useCallback(
        (event) => {
            if (event.button !== 0 || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey || !selectionController) return;
            if (event.target instanceof Element && event.target.closest(".rdg-editor-container")) return;

            const position = getCellPositionFromMouseEvent(event);
            if (!position || !canSelectDataCell(position.column)) return;

            event.preventDefault();
            mouseDownCellRef.current = {
                rowIdx: position.rowIdx,
                colIdx: position.colIdx,
                wasFocusedBeforeMouseDown: isSameCell(focusCellRef.current, position.rowIdx, position.colIdx),
                dragged: false,
            };
            dragSelectionRef.current = {
                start: position,
                last: position,
            };
            selectionController.setFocus(position.rowIdx, position.colIdx);
            selectFocusCell(position.rowIdx, position.colIdx, { suppressSelectedCellChange: true });
        },
        [getCellPositionFromMouseEvent, selectFocusCell, selectionController],
    );

    const handleGridMouseMove = useCallback(
        (event) => {
            const dragSelection = dragSelectionRef.current;
            if (!dragSelection || !selectionController) return;

            if (event.buttons !== 1) {
                finishDragSelection();
                return;
            }

            const position = getCellPositionFromMouseEvent(event);
            if (!position || !canSelectDataCell(position.column)) return;
            if (isSameCell(dragSelection.last, position.rowIdx, position.colIdx)) return;

            dragSelection.last = position;
            if (!isSameCell(dragSelection.start, position.rowIdx, position.colIdx)) {
                if (mouseDownCellRef.current) mouseDownCellRef.current.dragged = true;
                event.preventDefault();
            }
            selectionController.selectRange(dragSelection.start.rowIdx, dragSelection.start.colIdx, position.rowIdx, position.colIdx);
            selectFocusCell(position.rowIdx, position.colIdx, { suppressSelectedCellChange: true });
        },
        [finishDragSelection, getCellPositionFromMouseEvent, selectFocusCell, selectionController],
    );

    const handleCellDoubleClick = useCallback(
        (args, event) => {
            onCellDoubleClick?.(args, event);
            if (event.isGridDefaultPrevented()) return;

            const rowIdx = getRowIndex(rowsRef.current, args.row, rowKeyGetter);
            const colIdx = args.column.idx;
            if (rowIdx < 0 || !isGridCellEditable(rowIdx, colIdx)) return;

            const nextFocusCell = { rowIdx, colIdx };
            syncFocusCell(rowIdx, colIdx, args.row, args.column);
            if (enterEdit(rowIdx, colIdx, { focusCell: nextFocusCell })) {
                event.preventGridDefault();
                args.selectCell(true);
            }
        },
        [enterEdit, isGridCellEditable, onCellDoubleClick, rowKeyGetter, syncFocusCell],
    );

    const handleCellKeyDown = useCallback(
        (args, event) => {
            onCellKeyDown?.(args, event);
            if (event.isGridDefaultPrevented()) return;

            const rowIdx = args.rowIdx;
            const colIdx = args.column.idx;

            if (args.mode === "EDIT") {
                if (event.nativeEvent?.isComposing) return;
                if (event.key === "Escape") {
                    pendingNavigationRef.current = null;
                    scheduleAfterPaint(() => editRuntimeRef.current?.exitEdit?.());
                    return;
                }
                if (event.key === "Enter") {
                    queueNavigationAfterNativeEdit("enter", rowIdx, colIdx);
                    commitCurrentEditorDomValue(args, event);
                    scheduleAfterPaint(() => editRuntimeRef.current?.exitEdit?.());
                    return;
                }
                if (event.key === "Tab") {
                    queueNavigationAfterNativeEdit(event.shiftKey ? "shiftTab" : "tab", rowIdx, colIdx);
                    commitCurrentEditorDomValue(args, event);
                    scheduleAfterPaint(() => editRuntimeRef.current?.exitEdit?.());
                }
                return;
            }

            if (args.mode !== "SELECT") return;

            if (event.key === "Enter") {
                const target = getNavigationTarget("enter", rowIdx, colIdx);
                event.preventDefault();
                event.preventGridDefault();
                if (target) args.selectCell({ rowIdx: target.rowIdx, idx: target.colIdx });
                return;
            }

            if (event.key === "Tab") {
                const target = getNavigationTarget(event.shiftKey ? "shiftTab" : "tab", rowIdx, colIdx);
                event.preventDefault();
                event.preventGridDefault();
                if (!target) return;
                args.selectCell({ rowIdx: target.rowIdx, idx: target.colIdx });
                return;
            }

            if (!isKeyboardEditTrigger(event)) {
                if (event.key === "Delete" || event.key === "F2") event.preventGridDefault();
                return;
            }

            if (!isSameCell(focusCellRef.current, rowIdx, colIdx) || !isGridCellEditable(rowIdx, colIdx)) return;

            if (enterEdit(rowIdx, colIdx, { focusCell: focusCellRef.current, initialValue: getKeyboardInitialValue(event) })) {
                event.preventDefault();
                event.preventGridDefault();
                args.selectCell({ rowIdx, idx: colIdx }, true);
            }
        },
        [commitCurrentEditorDomValue, enterEdit, getNavigationTarget, isGridCellEditable, onCellKeyDown, queueNavigationAfterNativeEdit],
    );

    return (
        <GridTooltipProvider showTooltip={showTooltip} hideTooltip={hideTooltip}>
            <div
                ref={containerRef}
                className={joinClassNames("grid-core-shell", scrollLocked && "grid-core-shell-scroll-locked", className)}
                onMouseDownCapture={handleGridMouseDownCapture}
                onMouseMove={handleGridMouseMove}
                style={{
                    height,
                    "--grid-core-row-gap-height": `${normalizedRowGapHeight}px`,
                    "--grid-core-row-gap-color": rowGapColor,
                    ...style,
                }}
            >
                <GridStateProvider store={gridStateStore}>
                    <DataGrid
                        ref={dataGridRef}
                        className={joinClassNames("grid-core-rdg rdg-light", gridClassName)}
                        style={{ blockSize: "100%" }}
                        columns={enhancedColumns}
                        rows={rows}
                        rowKeyGetter={rowKeyGetter}
                        rowHeight={getRowHeight}
                        rowClass={getRowClass}
                        headerRowHeight={headerRowHeight}
                        enableVirtualization
                        renderers={mergedRenderers}
                        onRowsChange={handleRowsChange}
                        onCellClick={handleCellClick}
                        onCellDoubleClick={handleCellDoubleClick}
                        onCellKeyDown={handleCellKeyDown}
                        onSelectedCellChange={handleSelectedCellChange}
                        {...(onColumnResize ? { onColumnResize: handleColumnResize } : {})}
                        defaultColumnOptions={{ resizable: true }}
                        {...dataGridProps}
                    />
                </GridStateProvider>
            </div>
            {tooltip}
        </GridTooltipProvider>
    );
});

export default GridShell;
